import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import { Ingrediente, CriarIngredienteRequest, RegistrarMovimentacaoRequest } from '../types';
import toast from 'react-hot-toast';
import { extractErrorMessage } from '../utils/errorHandler';

export const useIngredientes = () => {
  return useQuery({
    queryKey: ['ingredientes'],
    queryFn: async () => {
      const response = await api.get<{ ingredientes: Ingrediente[] }>('/ingredientes');
      return response.data.ingredientes;
    },
  });
};

export const useIngrediente = (id: number) => {
  return useQuery({
    queryKey: ['ingrediente', id],
    queryFn: async () => {
      const response = await api.get<Ingrediente>(`/ingredientes/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCriarIngrediente = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CriarIngredienteRequest) => {
      const response = await api.post('/ingredientes', request);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredientes'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Ingrediente criado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao criar ingrediente'));
    },
  });
};

export const useAtualizarIngrediente = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: CriarIngredienteRequest }) => {
      const response = await api.put(`/ingredientes/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredientes'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Ingrediente atualizado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao atualizar ingrediente'));
    },
  });
};

export const useRemoverIngrediente = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/ingredientes/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredientes'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Ingrediente removido com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao remover ingrediente'));
    },
  });
};

export const useRegistrarMovimentacao = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: RegistrarMovimentacaoRequest) => {
      const response = await api.post(`/ingredientes/${request.ingredienteId}/movimentacao`, {
        tipo: request.tipo,
        quantidade: request.quantidade,
        motivo: request.motivo,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredientes'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Movimentação registrada com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao registrar movimentação'));
    },
  });
};
